import Phaser from 'phaser';
import { EntityManager, Monster, Tower, MonsterType, TowerType } from '../entities';
import { PathingSystem } from './PathingSystem';
import { WaveSpawner } from './WaveSpawner';
import { SessionManager } from './SessionManager';
import { DropSystem } from './DropSystem';
import { RENDER_LAYERS } from './RenderSystem';
import { COLORS } from '../constants';

// ============================================
// Debug System
// Based on towers_monsters_paths_design.md
// ============================================

interface DebugConfig {
  showPaths: boolean;
  showTowerRanges: boolean;
  showResistIcons: boolean;
  showMonsterHP: boolean;
  showFPS: boolean;
  showEntityCount: boolean;
  godMode: boolean;
  infiniteGems: boolean;
}

class DebugSystemManager {
  private scene: Phaser.Scene | null = null;
  private graphics: Phaser.GameObjects.Graphics | null = null;
  private debugText: Phaser.GameObjects.Text | null = null;
  private fpsText: Phaser.GameObjects.Text | null = null;
  private controlsPanel: Phaser.GameObjects.Container | null = null;

  private config: DebugConfig = {
    showPaths: false,
    showTowerRanges: false,
    showResistIcons: true,
    showMonsterHP: true,
    showFPS: true,
    showEntityCount: true,
    godMode: false,
    infiniteGems: false,
  };

  private isEnabled: boolean = false;
  private isUIVisible: boolean = false;

  init(scene: Phaser.Scene): void {
    this.scene = scene;
    this.graphics = scene.add.graphics();
    this.graphics.setDepth(RENDER_LAYERS.UI_WORLD - 1);

    // FPS display
    this.fpsText = scene.add.text(10, 10, '', {
      fontSize: '14px',
      color: '#00ff00',
      backgroundColor: '#000000aa',
      padding: { x: 5, y: 3 },
    });
    this.fpsText.setScrollFactor(0);
    this.fpsText.setDepth(RENDER_LAYERS.UI_SCREEN + 200);
    this.fpsText.setVisible(false);

    // Debug text display
    this.debugText = scene.add.text(10, 35, '', {
      fontSize: '12px',
      color: '#ffffff',
      backgroundColor: '#000000aa',
      padding: { x: 5, y: 3 },
    });
    this.debugText.setScrollFactor(0);
    this.debugText.setDepth(RENDER_LAYERS.UI_SCREEN + 200);
    this.debugText.setVisible(false);

    // Keyboard shortcuts
    this.setupKeyboardShortcuts();

    console.log('[DebugSystem] Initialized. Press F1 to toggle debug mode.');
  }

  private setupKeyboardShortcuts(): void {
    if (!this.scene) return;

    // F1 - Toggle debug mode
    this.scene.input.keyboard?.on('keydown-F1', () => {
      this.toggle();
    });

    // F2 - Toggle path display
    this.scene.input.keyboard?.on('keydown-F2', () => {
      if (this.isEnabled) {
        this.config.showPaths = !this.config.showPaths;
        console.log(`[Debug] Show paths: ${this.config.showPaths}`);
      }
    });

    // F3 - Toggle tower ranges
    this.scene.input.keyboard?.on('keydown-F3', () => {
      if (this.isEnabled) {
        this.config.showTowerRanges = !this.config.showTowerRanges;
        this.updateTowerRanges();
        console.log(`[Debug] Show tower ranges: ${this.config.showTowerRanges}`);
      }
    });

    // F4 - Spawn test wave
    this.scene.input.keyboard?.on('keydown-F4', () => {
      if (this.isEnabled) {
        this.spawnTestWave();
      }
    });

    // F5 - Toggle god mode
    this.scene.input.keyboard?.on('keydown-F5', () => {
      if (this.isEnabled) {
        this.config.godMode = !this.config.godMode;
        console.log(`[Debug] God mode: ${this.config.godMode}`);
      }
    });

    // F6 - Toggle infinite gems
    this.scene.input.keyboard?.on('keydown-F6', () => {
      if (this.isEnabled) {
        this.config.infiniteGems = !this.config.infiniteGems;
        if (this.config.infiniteGems) {
          SessionManager.addGems(9999);
        }
        console.log(`[Debug] Infinite gems: ${this.config.infiniteGems}`);
      }
    });

    // F7 - Kill all monsters
    this.scene.input.keyboard?.on('keydown-F7', () => {
      if (this.isEnabled) {
        this.killAllMonsters();
      }
    });

    // F8 - Skip to next wave
    this.scene.input.keyboard?.on('keydown-F8', () => {
      if (this.isEnabled) {
        this.skipToNextWave();
      }
    });
  }

  toggle(): void {
    this.isEnabled = !this.isEnabled;

    if (this.fpsText) this.fpsText.setVisible(this.isEnabled && this.config.showFPS);
    if (this.debugText) this.debugText.setVisible(this.isEnabled && this.config.showEntityCount);

    if (!this.isEnabled) {
      this.graphics?.clear();
      this.updateTowerRanges();
    }

    console.log(`[DebugSystem] Debug mode: ${this.isEnabled ? 'ON' : 'OFF'}`);
  }

  update(delta: number): void {
    if (!this.isEnabled || !this.scene) return;

    // Update FPS
    if (this.config.showFPS && this.fpsText) {
      const fps = Math.round(this.scene.game.loop.actualFps);
      this.fpsText.setText(`FPS: ${fps}`);
    }

    // Update entity count
    if (this.config.showEntityCount && this.debugText) {
      const monsterCount = EntityManager.getCount('monster');
      const towerCount = EntityManager.getCount('tower');
      const wave = WaveSpawner.getCurrentWave();
      const total = WaveSpawner.getTotalWaves();

      this.debugText.setText([
        `Wave: ${wave}/${total}`,
        `Monsters: ${monsterCount}`,
        `Towers: ${towerCount}`,
        `Gems: ${SessionManager.getGems()}`,
        `Villagers: ${SessionManager.getVillagers()}`,
        '',
        'F1: Toggle debug',
        'F2: Show paths',
        'F3: Tower ranges',
        'F4: Spawn test',
        'F5: God mode',
        'F6: Infinite gems',
        'F7: Kill all',
        'F8: Skip wave',
      ].join('\n'));
    }

    // Draw debug graphics
    this.drawDebug();

    // God mode - keep villagers alive
    if (this.config.godMode && SessionManager.getVillagers() < 5) {
      // Restore villagers if low
      for (let i = 0; i < 5; i++) {
        SessionManager.removeVillager(-1); // Negative = add
      }
    }

    // Infinite gems
    if (this.config.infiniteGems && SessionManager.getGems() < 100) {
      SessionManager.addGems(100);
    }
  }

  private drawDebug(): void {
    if (!this.graphics) return;
    this.graphics.clear();

    // Draw paths
    if (this.config.showPaths) {
      PathingSystem.enableDebugDraw(this.graphics);
      PathingSystem.drawDebugPaths();
    }

    // Draw monster info
    if (this.config.showMonsterHP) {
      this.drawMonsterInfo();
    }
  }

  private drawMonsterInfo(): void {
    if (!this.graphics) return;

    const monsters = EntityManager.getByType<Monster>('monster');

    for (const monster of monsters) {
      // Draw movement type indicator
      if (monster.getMovementType() === 'air') {
        this.graphics.lineStyle(1, 0x00ffff, 0.5);
        this.graphics.strokeCircle(monster.x, monster.y, 15);
      }

      // Draw path progress
      const progress = monster.getPathProgress();
      this.graphics.fillStyle(0xffffff, 0.7);
      this.graphics.fillRect(monster.x - 15, monster.y + 15, 30 * (progress / 1000), 3);
    }
  }

  private updateTowerRanges(): void {
    const towers = EntityManager.getByType<Tower>('tower');

    for (const tower of towers) {
      tower.showRange(this.isEnabled && this.config.showTowerRanges);
    }
  }

  // ============================================
  // Debug Actions
  // ============================================

  spawnTestWave(): void {
    if (!this.scene) return;

    console.log('[Debug] Spawning test wave...');

    const monsterTypes: MonsterType[] = ['crawler', 'runner', 'brute', 'shielded', 'bat', 'flyer'];
    const pathStart = PathingSystem.getPathStart('main');

    if (!pathStart) {
      console.warn('[Debug] No path found for test spawn');
      return;
    }

    // Spawn one of each type
    for (let i = 0; i < monsterTypes.length; i++) {
      const type = monsterTypes[i];
      const routeId = PathingSystem.selectRoute('main');

      this.scene.time.delayedCall(i * 500, () => {
        const startPos = PathingSystem.getPathStart('main', routeId);
        if (!startPos) return;

        const monster = new Monster(this.scene!, {
          id: `debug-monster-${Date.now()}-${i}`,
          x: startPos.x,
          y: startPos.y,
          monsterType: type,
          pathId: 'main',
          routeId,
        });

        monster.onReachedBase = () => {
          if (!this.config.godMode) {
            SessionManager.removeVillager(monster.getDamage());
          }
        };

        monster.onDeath = () => {
          // Debug monsters drop items that need to be picked up
          DropSystem.spawnMonsterDrops(type, monster.x, monster.y);
        };

        EntityManager.add(monster);
        console.log(`[Debug] Spawned ${type}`);
      });
    }
  }

  killAllMonsters(): void {
    const monsters = EntityManager.getByType<Monster>('monster');
    console.log(`[Debug] Killing ${monsters.length} monsters...`);

    for (const monster of monsters) {
      monster.takeDamage(9999);
    }
  }

  skipToNextWave(): void {
    // Kill remaining monsters and trigger wave completion
    this.killAllMonsters();
    console.log('[Debug] Skipping to next wave...');
  }

  spawnMonster(type: MonsterType, routeId?: string): void {
    if (!this.scene) return;

    const route = routeId || PathingSystem.selectRoute('main');
    const startPos = PathingSystem.getPathStart('main', route);

    if (!startPos) return;

    const monster = new Monster(this.scene, {
      id: `debug-monster-${Date.now()}`,
      x: startPos.x,
      y: startPos.y,
      monsterType: type,
      pathId: 'main',
      routeId: route,
    });

    monster.onReachedBase = () => {
      if (!this.config.godMode) {
        SessionManager.removeVillager(monster.getDamage());
      }
    };

    monster.onDeath = () => {
      // Debug monsters drop items that need to be picked up
      DropSystem.spawnMonsterDrops(type, monster.x, monster.y);
    };

    EntityManager.add(monster);
    console.log(`[Debug] Spawned ${type} on route ${route}`);
  }

  // ============================================
  // Config Getters/Setters
  // ============================================

  isGodMode(): boolean {
    return this.config.godMode;
  }

  hasInfiniteGems(): boolean {
    return this.config.infiniteGems;
  }

  setConfig(key: keyof DebugConfig, value: boolean): void {
    this.config[key] = value;
  }

  getConfig(): DebugConfig {
    return { ...this.config };
  }

  // ============================================
  // Cleanup
  // ============================================

  destroy(): void {
    this.graphics?.destroy();
    this.fpsText?.destroy();
    this.debugText?.destroy();
    this.controlsPanel?.destroy();
    this.scene = null;
  }
}

// Singleton instance
export const DebugSystem = new DebugSystemManager();
